import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Lock, MapPin } from 'lucide-react';
import { DataGrid } from '@/components/DataGrid';
import { LiveScreen, QueryState, formatNumber, type Column } from '@/components/live';
import { StatCards } from '@/components/StatCards';
import { callRpc } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { can, isAdmin } from '@/auth/session';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';

interface RepoRow {
  id: string;
  name: string;
  local_name: string | null;
  official_code: string | null;
  geography_type: string;
  parent_id: string | null;
  is_activated: boolean;
  effective_from: string | null;
  effective_to: string | null;
  child_total: number;
  child_activated: number;
  farmer_count: number;
  plot_count: number;
}

/** The ladder, and the word each rung is called on screen. */
const LEVELS = [
  { type: 'state', label: 'State', plural: 'states' },
  { type: 'district', label: 'District', plural: 'districts' },
  { type: 'block', label: 'Sub-district', plural: 'sub-districts' },
  { type: 'village', label: 'Village', plural: 'villages' },
] as const;

const childLevel = (type: string) => LEVELS[LEVELS.findIndex((level) => level.type === type) + 1] ?? null;
const levelOf = (type: string) => LEVELS.find((level) => level.type === type) ?? LEVELS[0];

/**
 * Which places this organisation works in (owner, 2026-09-25: "out geo-repository of database ui
 * is needed as a seperate page?").
 *
 * The geography master holds 18,811 places and this organisation works in a handful of them. That
 * handful is not a preference - it is the gate every picker in the application reads. The farmer's
 * village, the plot's location, the geography scope cascade, the free-text place search: all of
 * them list activated places and nothing else, which is what stops a farmer being enrolled into a
 * district nobody has taken on.
 *
 * Until this page, nothing in the application could open that gate. The screen that would have was
 * one of the fourteen static prototypes removed on 2026-09-25 - it showed West Bengal, Darjeeling
 * and Mirik whatever the database said - so starting work in a new district meant an INSERT by
 * hand, and an organisation without database access could not start work in a new area at all.
 *
 * It is a drill-down rather than one long list because 18,811 rows is not a list, and because the
 * question is always asked downwards: which districts of this state, which sub-districts of that
 * district.
 */
export default function GeographyScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  // Where we are. Empty is the top: every state in the master.
  const [trail, setTrail] = useState<Array<{ id: string; name: string; type: string }>>([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const here = trail[trail.length - 1] ?? null;
  const level = here ? childLevel(here.type) ?? levelOf('village') : LEVELS[0];
  // The grant AND tier 0/1, as both row policies on `geo.organization_geographies` ask. An admin
  // without the grant is refused by the policy, and a manager holding it is stopped by the tier.
  const mayChange = isAdmin(profile) && can(profile, 'geography.activation', 'update')
    && can(profile, 'geography.activation', 'create');

  const rows = useQuery({
    queryKey: ['geo-repo', level.type, here?.id ?? null, search],
    queryFn: () => callRpc<RepoRow[]>('fn_geography_repository', {
      p_parent_id: here?.id ?? null, p_geography_type: level.type, p_search: search || null, p_limit: 400,
    }),
    enabled: isSupabaseConfigured,
  });

  const all = useMemo(() => rows.data ?? [], [rows.data]);

  async function toggle(row: RepoRow) {
    if (!mayChange) return;
    const next = !row.is_activated;
    if (!next) {
      // Switching a place off takes everything under it with it, and stops anyone recording a
      // new farmer there. Both are worth saying before it happens rather than after.
      const under = row.child_activated ? `${row.child_activated} activated ${childLevel(row.geography_type)?.plural ?? 'places'} under it` : '';
      const held = row.farmer_count || row.plot_count
        ? `${row.farmer_count} farmer${row.farmer_count === 1 ? '' : 's'} and ${row.plot_count} plot${row.plot_count === 1 ? '' : 's'} are already recorded there`
        : '';
      const detail = [under, held].filter(Boolean).join(', and ');
      const message = `Stop working in ${row.name}?${detail ? `\n\n${detail}.` : ''}`
        + '\n\nNobody will be able to record a new farmer or plot there. Records already made stay where they are and stay visible.';
      if (!window.confirm(message)) return;
    }
    setBusy(row.id);
    try {
      const result = await callRpc<Array<{ activated: number; deactivated: number }>>(
        'fn_set_geography_activation', { p_geography_id: row.id, p_active: next });
      const counts = Array.isArray(result) ? result[0] : result;
      await queryClient.invalidateQueries({ queryKey: ['geo-repo'] });
      // Every other geography list in the app is now different, including the pickers a form
      // already on screen is holding.
      await queryClient.invalidateQueries({ queryKey: ['geo-any'] });
      await queryClient.invalidateQueries({ queryKey: ['geo-children'] });
      await queryClient.invalidateQueries({ queryKey: ['geo'] });
      if (next) {
        const above = (counts?.activated ?? 1) - 1;
        toast.success(`${row.name} is activated${above > 0 ? `, with ${above} place${above === 1 ? '' : 's'} above it` : ''}.`);
      } else {
        const also = (counts?.deactivated ?? 1) - 1;
        toast.success(`${row.name} is switched off${also > 0 ? `, with ${also} place${also === 1 ? '' : 's'} under it` : ''}.`);
      }
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'That could not be changed.');
    } finally {
      setBusy(null);
    }
  }

  const next = childLevel(level.type);
  const columns: Array<Column<RepoRow>> = [
    { key: 'name', label: level.label, render: (row) => (
      <span className="cell-stack">
        <strong>{row.name}</strong>
        <small className="muted">{[row.local_name && row.local_name !== row.name ? row.local_name : null, row.official_code].filter(Boolean).join(' · ')}</small>
      </span>
    ) },
    ...(next ? [{
      key: 'child_activated',
      label: next.label + 's',
      align: 'right' as const,
      render: (row: RepoRow) => (row.child_total
        ? <span className={row.child_activated ? undefined : 'muted'}>{row.child_activated} of {row.child_total}</span>
        : <span className="muted">--</span>),
    }] : []),
    { key: 'farmer_count', label: 'Farmers', align: 'right', render: (row) => (row.farmer_count ? formatNumber(row.farmer_count, 0) : <span className="muted">--</span>) },
    { key: 'plot_count', label: 'Plots', align: 'right', render: (row) => (row.plot_count ? formatNumber(row.plot_count, 0) : <span className="muted">--</span>) },
    { key: 'is_activated', label: 'Worked in', render: (row) => (
      <button
        aria-label={`${row.name}: ${row.is_activated ? 'activated' : 'not activated'}`}
        aria-pressed={row.is_activated}
        className="onoff-toggle"
        disabled={!mayChange || busy !== null}
        onClick={(event) => { event.stopPropagation(); void toggle(row); }}
        title={mayChange
          ? row.is_activated ? `Stop working in ${row.name}` : `Take on ${row.name}`
          : 'Changing this needs an organisation admin who manages the geography repository.'}
        type="button"
      >
        {busy === row.id ? '...' : row.is_activated ? 'On' : 'Off'}
      </button>
    ) },
    ...(next ? [{
      key: 'open',
      label: '',
      render: (row: RepoRow) => (row.child_total ? (
        <button onClick={(event) => { event.stopPropagation(); open(row); }} type="button">
          {next.label}s <ChevronRight size={13} />
        </button>
      ) : null),
    }] : []),
  ];

  function open(row: RepoRow) {
    setTrail([...trail, { id: row.id, name: row.name, type: row.geography_type }]);
    setSearch('');
  }

  const activated = all.filter((row) => row.is_activated).length;

  return (
    <LiveScreen
      subtitle="The places this organisation works in. Every village, plot and place dropdown in the application lists these and nothing else."
      title="Geography repository"
    >
      {!mayChange && (
        <p className="offline-banner">
          <Lock size={13} /> Viewing only. Taking on a place needs an organisation admin who
          manages the geography repository.
        </p>
      )}

      <nav aria-label="Where you are" className="toolbar-row">
        <button disabled={!trail.length} onClick={() => { setTrail([]); setSearch(''); }} type="button">
          <MapPin size={14} /> All states
        </button>
        {trail.map((step, index) => (
          <span key={step.id}>
            <ChevronRight aria-hidden size={13} />
            <button
              disabled={index === trail.length - 1}
              onClick={() => { setTrail(trail.slice(0, index + 1)); setSearch(''); }}
              type="button"
            >
              {step.name}
            </button>
          </span>
        ))}
      </nav>

      <StatCards
        active={null}
        onSelect={() => {}}
        rows={all}
        stats={[
          { key: 'all', label: here ? `${levelOf(level.type).label}s in ${here.name}` : 'States in the master', tone: 'neutral', match: () => true },
          { key: 'on', label: 'Worked in', tone: 'ok', match: (row: RepoRow) => row.is_activated },
          { key: 'off', label: 'Not taken on', tone: 'neutral', match: (row: RepoRow) => !row.is_activated },
          { key: 'used', label: 'With records', tone: 'info', match: (row: RepoRow) => row.farmer_count > 0 || row.plot_count > 0 },
        ]}
      />

      <QueryState empty={`No ${level.plural} found.`} query={rows}>
        <DataGrid
          columns={columns}
          emptyMessage={search ? `No ${level.plural} match "${search}".` : `No ${level.plural} here.`}
          exportName={`geography-${level.type}`}
          onRowClick={next ? (row) => { if (row.child_total) open(row); } : undefined}
          onSearchChange={setSearch}
          pageSize={50}
          rows={all}
          screenKey="geography.activation"
          search={search}
          searchPlaceholder={`Search ${level.plural} by name or code`}
        />
      </QueryState>

      <p className="state-note">
        {activated === 0
          ? `None of these ${level.plural} is taken on yet. Nothing can be recorded in them until one is.`
          : `${activated} of ${all.length} ${level.plural} here are worked in. Taking on a place also takes on the places above it; switching one off also switches off everything under it.`}
      </p>
    </LiveScreen>
  );
}
